import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ParticipantAnalyticsEntry,
  ParticipantAnalyticsFilters,
  ParticipantAnalyticsMetrics,
  ParticipantAnalyticsPagination,
  ParticipantSegmentation,
  DateRangePeriod,
  ParticipantStatus,
} from "../types/analytics";
import participantsAnalyticsApi, {
  ParticipantAnalytics,
} from "../services/api/participants-analytics.api";
import { logger } from "../utils/logger";
import {
  type AnalyticsScope,
  getScopedParticipants,
  getScopedParticipantMetrics,
  getScopedParticipantSegmentation,
} from "../services/api/analytics-router";

// ─── Shared arg helpers ───────────────────────────────────────────────────────

export type ScopedArg<T> = T | { filters: T; scope: AnalyticsScope };

export function extractArg<T extends object>(
  arg: ScopedArg<T> | undefined,
  defaults: T,
): { filters: T; scope: AnalyticsScope } {
  if (!arg) return { filters: defaults, scope: "platform" };
  if ("scope" in arg) return arg as { filters: T; scope: AnalyticsScope };
  return { filters: arg as T, scope: "platform" };
}

// ─── Normalizers ──────────────────────────────────────────────────────────────

export const normalizeParticipantAnalyticsEntry = (
  entry: ParticipantAnalytics,
): ParticipantAnalyticsEntry => ({
  id: entry.id,
  name: entry.name,
  email: entry.email,
  avatar: entry.avatar ?? "",
  registrationDate: entry.registrationDate,
  eventsAttended: entry.eventsAttended ?? 0,
  totalSpent: entry.totalSpent ?? 0,
  averageRating: entry.averageRating ?? 0,
  location: entry.location,
  status: entry.status ?? ParticipantStatus.ACTIVE,
  lastActivity: entry.lastActivity,
  eventsRegistered: entry.eventsRegistered ?? 0,
  eventsCompleted: entry.eventsCompleted ?? 0,
  averageEventRating: entry.averageEventRating ?? 0,
  referralCount: entry.referralCount ?? 0,
  totalEarnings: entry.totalEarnings ?? 0,
  membershipDuration: entry.membershipDuration ?? 0,
  engagementScore: entry.engagementScore ?? 0,
});

export const normalizeParticipantMetrics = (
  metrics: Partial<ParticipantAnalyticsMetrics>,
): ParticipantAnalyticsMetrics => ({
  overview: {
    totalParticipants: Number(metrics?.overview?.totalParticipants ?? 0),
    newParticipants: Number(metrics?.overview?.newParticipants ?? 0),
    activeParticipants: Number(metrics?.overview?.activeParticipants ?? 0),
    vipParticipants: Number(metrics?.overview?.vipParticipants ?? 0),
    inactiveParticipants: Number(metrics?.overview?.inactiveParticipants ?? 0),
  },
  engagement: {
    averageEventsPerParticipant: Number(
      metrics?.engagement?.averageEventsPerParticipant ?? 0,
    ),
    averageSpendingPerParticipant: Number(
      metrics?.engagement?.averageSpendingPerParticipant ?? 0,
    ),
    averageRating: Number(metrics?.engagement?.averageRating ?? 0),
    retentionRate: Number(metrics?.engagement?.retentionRate ?? 0),
    churnRate: Number(metrics?.engagement?.churnRate ?? 0),
  },
  financial: {
    totalRevenue: Number(metrics?.financial?.totalRevenue ?? 0),
    averageRevenuePerParticipant: Number(
      metrics?.financial?.averageRevenuePerParticipant ?? 0,
    ),
    topSpenders: Number(metrics?.financial?.topSpenders ?? 0),
    revenueGrowth: Number(metrics?.financial?.revenueGrowth ?? 0),
  },
  activity: {
    averageSessionDuration: Number(
      metrics?.activity?.averageSessionDuration ?? 0,
    ),
    averageLoginFrequency: Number(
      metrics?.activity?.averageLoginFrequency ?? 0,
    ),
    mostActiveTime: String(metrics?.activity?.mostActiveTime ?? ""),
    mostActiveDay: String(metrics?.activity?.mostActiveDay ?? ""),
  },
  trends: {
    registrationTrend: (metrics?.trends?.registrationTrend ?? []).map(
      (item) => ({
        date: String(item?.date ?? ""),
        count: Number(item?.count ?? 0),
      }),
    ),
    activityTrend: (metrics?.trends?.activityTrend ?? []).map((item) => ({
      date: String(item?.date ?? ""),
      activeUsers: Number(item?.activeUsers ?? 0),
    })),
    revenueTrend: (metrics?.trends?.revenueTrend ?? []).map((item) => ({
      date: String(item?.date ?? ""),
      revenue: Number(item?.revenue ?? 0),
    })),
  },
});

export const normalizeParticipantSegmentation = (
  segmentation: Partial<ParticipantSegmentation> | null | undefined,
): ParticipantSegmentation | null => {
  if (!segmentation) return null;
  return {
    byAge: (segmentation?.byAge ?? []).map((item) => ({
      ageGroup: String(item?.ageGroup ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
    byLocation: (segmentation?.byLocation ?? []).map((item) => ({
      location: String(item?.location ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
    byActivity: (segmentation?.byActivity ?? []).map((item) => ({
      activityLevel: String(item?.activityLevel ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
    bySpending: (segmentation?.bySpending ?? []).map((item) => ({
      spendingLevel: String(item?.spendingLevel ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
    byEngagement: (segmentation?.byEngagement ?? []).map((item) => ({
      engagementLevel: String(item?.engagementLevel ?? ""),
      count: Number(item?.count ?? 0),
      percentage: Number(item?.percentage ?? 0),
    })),
  };
};

export const normalizeParticipantFilters = (
  filters: ParticipantAnalyticsFilters = {},
): ParticipantAnalyticsFilters => {
  const result: ParticipantAnalyticsFilters = {
    dateRange: filters.dateRange ?? DateRangePeriod.LAST_30,
    page: filters.page ?? 1,
    limit: filters.limit ?? 10,
  };
  if (filters.eventId !== undefined) result.eventId = filters.eventId;
  if (filters.status !== undefined) result.status = filters.status;
  if (filters.location !== undefined) result.location = filters.location;
  return result;
};

export const normalizeParticipantPagination = (
  pagination: Partial<ParticipantAnalyticsPagination> | null | undefined,
): ParticipantAnalyticsPagination | null => {
  if (!pagination) return null;
  return {
    page: Number(pagination?.page ?? 1),
    limit: Number(pagination?.limit ?? 10),
    total: Number(pagination?.total ?? 0),
    totalPages: Number(pagination?.totalPages ?? 0),
  };
};

// ─── API payload shape ────────────────────────────────────────────────────────

interface ParticipantsApiShape {
  participants?: ParticipantAnalytics[];
  pagination?: Partial<ParticipantAnalyticsPagination>;
  filters?: ParticipantAnalyticsFilters;
}
interface ParticipantsApiPayload extends ParticipantsApiShape {
  data?: ParticipantsApiShape;
}

// ─── State ────────────────────────────────────────────────────────────────────

export interface ParticipantsAnalyticsState {
  items: ParticipantAnalyticsEntry[];
  metrics: ParticipantAnalyticsMetrics | null;
  segmentation: ParticipantSegmentation | null;
  filters: ParticipantAnalyticsFilters;
  pagination: ParticipantAnalyticsPagination | null;
  loading: {
    list: boolean;
    metrics: boolean;
    segmentation: boolean;
  };
  error: string | null;
}

const initialState: ParticipantsAnalyticsState = {
  items: [],
  metrics: null,
  segmentation: null,
  filters: {},
  pagination: null,
  loading: { list: false, metrics: false, segmentation: false },
  error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchParticipantAnalytics = createAsyncThunk(
  "participants/fetchParticipantAnalytics",
  async (arg: ScopedArg<ParticipantAnalyticsFilters> = {}) => {
    try {
      const { filters, scope } = extractArg(
        arg as
          | ParticipantAnalyticsFilters
          | { filters: ParticipantAnalyticsFilters; scope: AnalyticsScope },
        {},
      );
      if (scope !== "platform") {
        const result = await getScopedParticipants(scope, filters);
        return {
          participants: result.participants,
          pagination: normalizeParticipantPagination(result.pagination),
          filters: normalizeParticipantFilters(result.filters),
        };
      }
      const response = await participantsAnalyticsApi.getParticipants(filters);
      const payload = response.data as ParticipantsApiPayload;
      const result = payload?.data ?? payload;
      const rawParticipants: ParticipantAnalytics[] =
        result?.participants ?? [];
      const pagination = result?.pagination ?? payload?.pagination ?? null;
      const responseFilters = result?.filters ?? payload?.filters ?? filters;
      return {
        participants: rawParticipants.map(normalizeParticipantAnalyticsEntry),
        pagination: normalizeParticipantPagination(pagination),
        filters: normalizeParticipantFilters(responseFilters),
      };
    } catch (error) {
      logger.error("Не удалось загрузить аналитику участников", error);
      throw error;
    }
  },
);

export const fetchParticipantMetrics = createAsyncThunk(
  "participants/fetchParticipantMetrics",
  async (arg: ScopedArg<ParticipantAnalyticsFilters> = {}) => {
    try {
      const { filters, scope } = extractArg(
        arg as
          | ParticipantAnalyticsFilters
          | { filters: ParticipantAnalyticsFilters; scope: AnalyticsScope },
        {},
      );
      if (scope !== "platform") {
        return await getScopedParticipantMetrics(scope, filters);
      }
      const response =
        await participantsAnalyticsApi.getParticipantsMetrics(filters);
      return normalizeParticipantMetrics(
        response.data as Partial<ParticipantAnalyticsMetrics>,
      );
    } catch (error) {
      logger.error("Не удалось получить метрики участников", error);
      throw error;
    }
  },
);

export const fetchParticipantSegmentation = createAsyncThunk(
  "participants/fetchParticipantSegmentation",
  async (arg: ScopedArg<ParticipantAnalyticsFilters> = {}) => {
    try {
      const { filters, scope } = extractArg(
        arg as
          | ParticipantAnalyticsFilters
          | { filters: ParticipantAnalyticsFilters; scope: AnalyticsScope },
        {},
      );
      if (scope !== "platform") {
        return await getScopedParticipantSegmentation(scope, filters);
      }
      const response =
        await participantsAnalyticsApi.getParticipantsSegmentation(filters);
      return normalizeParticipantSegmentation(
        response.data as Partial<ParticipantSegmentation>,
      );
    } catch (error) {
      logger.error("Не удалось получить сегментацию участников", error);
      throw error;
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const participantsAnalyticsSlice = createSlice({
  name: "participants",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchParticipantAnalytics.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchParticipantAnalytics.fulfilled, (state, action) => {
        state.loading.list = false;
        state.items = action.payload.participants;
        state.pagination = action.payload.pagination;
        state.filters = action.payload.filters;
      })
      .addCase(fetchParticipantAnalytics.rejected, (state, action) => {
        state.loading.list = false;
        state.error =
          action.error.message ?? "Ошибка загрузки аналитики участников";
      })

      .addCase(fetchParticipantMetrics.pending, (state) => {
        state.loading.metrics = true;
      })
      .addCase(fetchParticipantMetrics.fulfilled, (state, action) => {
        state.loading.metrics = false;
        state.metrics = action.payload;
      })
      .addCase(fetchParticipantMetrics.rejected, (state, action) => {
        state.loading.metrics = false;
        state.error =
          action.error.message ?? "Ошибка загрузки метрик участников";
      })

      .addCase(fetchParticipantSegmentation.pending, (state) => {
        state.loading.segmentation = true;
      })
      .addCase(fetchParticipantSegmentation.fulfilled, (state, action) => {
        state.loading.segmentation = false;
        state.segmentation = action.payload;
      })
      .addCase(fetchParticipantSegmentation.rejected, (state, action) => {
        state.loading.segmentation = false;
        state.error =
          action.error.message ?? "Ошибка загрузки сегментации участников";
      }),
});

export default participantsAnalyticsSlice.reducer;
